import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { Wordmark } from "@/components/logo";
import { Button } from "@/components/ui";

const FRAMEWORKS = [
  { emoji: "🎯", name: "Learn a Topic", body: "Paste material, pick depth per subtopic, get a tailored set." },
  { emoji: "🔬", name: "Deep Dive", body: "Surface misconceptions and probe a topic from every angle." },
  { emoji: "➗", name: "Math Deep Dive", body: "Level-adaptive practice with prerequisites and transfer problems." },
  { emoji: "🇩🇪", name: "Deutsch Trainer", body: "Grammar, vocab, and reading drills tuned for German." },
  { emoji: "🎓", name: "Lecture · Exercise · Recap", body: "Turn a week of lectures into a guided review." },
  { emoji: "🧠", name: "Master a Trait", body: "Decompose a skill into principles and drill the recipe." },
];

const FEATURES = [
  { emoji: "✨", title: "AI question sets", body: "Frameworks generate mixed question types from your own material — not generic quizzes." },
  { emoji: "🎮", title: "Practice that counts", body: "Earn screen-time minutes for correct answers. Streaks, XP, and tiers keep momentum." },
  { emoji: "👥", title: "Learn together", body: "Clans, shared sets, chat, and co-op sessions make studying social." },
  { emoji: "📈", title: "See your growth", body: "Per-topic mastery, accuracy trends, and a live global knowledge tree." },
];

const STEPS = [
  { n: "1", title: "Generate", body: "Choose a framework and feed it your topic, slides, or problem set." },
  { n: "2", title: "Practise", body: "Answer adaptive questions across dozens of formats and review summary cards." },
  { n: "3", title: "Earn & track", body: "Bank screen-time, build streaks, and watch your mastery climb." },
];

function Header() {
  const { t } = useTranslation();
  const { status, user, logout } = useAuth();
  const displayName = user?.display_name || user?.username || user?.email || t("Learner");
  const onLogout = async () => {
    await logout();
  };
  return (
    <header className="sticky top-0 z-30 border-b border-black/5 bg-white/80 backdrop-blur">
      <div className="container-page flex h-16 items-center">
        <Wordmark className="text-lg text-ink" />
        <div className="ml-auto flex items-center gap-2">
          {status === "authenticated" ? (
            <>
              <span className="hidden text-sm text-ink-faint sm:inline">{displayName}</span>
              <Link to="/app">
                <Button size="sm">{t("Open app")}</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={onLogout}>
                {t("Sign out")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  {t("Sign in")}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm">{t("Get started")}</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-brand-200/40 blur-3xl" />
      <div className="container-page relative grid gap-12 py-20 lg:grid-cols-2 lg:items-center lg:py-28">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200">
            🎉 {t("Now on the web")}
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            {t("Turn study time into")}{" "}
            <span className="text-brand-600">{t("screen time.")}</span>
          </h1>
          <p className="mt-5 max-w-lg text-lg text-ink-soft">
            {t(
              "EduPause generates AI question sets from your own material, then rewards real practice with the minutes you want back. Same account, same progress — now in your browser.",
            )}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg">{t("Get started — it's free")}</Button>
            </Link>
            <a href="#how">
              <Button variant="secondary" size="lg">
                {t("See how it works")}
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-ink-faint">{t("No credit card. Works alongside the iOS app.")}</p>
        </div>

        <div className="animate-fade-up [animation-delay:120ms]">
          <div className="card mx-auto max-w-md p-6 shadow-glow">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-faint">{t("Today's session")}</span>
              <span className="rounded-full bg-accent-500/15 px-2.5 py-1 text-xs font-bold text-accent-600">
                {t("+24 min earned")}
              </span>
            </div>
            <div className="mt-4 space-y-3">
              {[
                { q: "What drives diffusion across a membrane?", ok: true },
                { q: "Match each term to its definition", ok: true },
                { q: "Order the steps of the Krebs cycle", ok: false },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-black/5 bg-brand-50/40 px-4 py-3"
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      row.ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {row.ok ? "✓" : "↻"}
                  </span>
                  <span className="text-sm text-ink-soft">{t(row.q)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-brand-100">
              <div className="h-full w-3/4 rounded-full bg-brand-500" />
            </div>
            <p className="mt-2 text-xs text-ink-faint">
              {t("{{correct}} of {{total}} correct · {{days}}-day streak", {
                correct: 9,
                total: 12,
                days: 7,
              })}{" "}
              🔥
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useTranslation();
  return (
    <section className="border-y border-black/5 bg-brand-50/30 py-20">
      <div className="container-page">
        <h2 className="text-center text-3xl font-bold tracking-tight">{t("Everything the app does")}</h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-ink-faint">
          {t("The full EduPause learning loop, faithfully mirrored for the web.")}
        </p>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="card p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl">
                {f.emoji}
              </div>
              <h3 className="mt-4 font-semibold">{t(f.title)}</h3>
              <p className="mt-1 text-sm text-ink-faint">{t(f.body)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  return (
    <section id="how" className="py-20">
      <div className="container-page">
        <h2 className="text-center text-3xl font-bold tracking-tight">{t("How it works")}</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-lg font-bold text-white">
                {s.n}
              </div>
              <h3 className="mt-4 text-lg font-semibold">{t(s.title)}</h3>
              <p className="mt-1 text-ink-faint">{t(s.body)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Frameworks() {
  const { t } = useTranslation();
  return (
    <section className="border-t border-black/5 bg-brand-50/30 py-20">
      <div className="container-page">
        <h2 className="text-center text-3xl font-bold tracking-tight">{t("Pick a framework")}</h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-ink-faint">
          {t("Purpose-built generators for the way you actually study.")}
        </p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FRAMEWORKS.map((fw) => (
            <div key={fw.name} className="card flex items-start gap-4 p-5">
              <span className="text-2xl">{fw.emoji}</span>
              <div>
                <h3 className="font-semibold">{t(fw.name)}</h3>
                <p className="mt-0.5 text-sm text-ink-faint">{t(fw.body)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { t } = useTranslation();
  return (
    <section className="py-20">
      <div className="container-page">
        <div className="relative overflow-hidden rounded-2xl bg-brand-700 px-8 py-16 text-center text-white shadow-glow">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-500/40 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl" />
          <h2 className="relative text-3xl font-bold tracking-tight sm:text-4xl">
            {t("Ready to earn your time back?")}
          </h2>
          <p className="relative mx-auto mt-3 max-w-md text-brand-100">
            {t("Create a free account and generate your first question set in minutes.")}
          </p>
          <div className="relative mt-8 flex justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-brand-700 hover:bg-brand-50">
                {t("Get started")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-black/5 py-10">
      <div className="container-page flex flex-col items-center justify-between gap-4 sm:flex-row">
        <Wordmark className="text-base text-ink" />
        <nav className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-faint">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
          <Link to="/impressum">Impressum</Link>
        </nav>
        <p className="text-sm text-ink-faint">
          {t("© {{year}} EduPause. All rights reserved.", { year: new Date().getFullYear() })}
        </p>
      </div>
    </footer>
  );
}

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Hero />
      <Features />
      <HowItWorks />
      <Frameworks />
      <CTA />
      <Footer />
    </div>
  );
}
